import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Mail, AlertCircle, Shield, Key, ArrowLeft, CheckCircle, Send } from 'lucide-react';
import axios from 'axios';
import { TwoFactorVerification } from '../components/TwoFactorVerification';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [pending2FAAdminId, setPending2FAAdminId] = useState(null);
  const [pending2FAEmail, setPending2FAEmail] = useState('');
  
  // Password reset state
  const [showReset, setShowReset] = useState(false);
  const [resetMode, setResetMode] = useState('email'); // 'email' or 'secret'
  const [resetEmail, setResetEmail] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        email,
        password
      });

      if (response.data.success) {
        // Check if 2FA is required
        if (response.data.requires_2fa) {
          setRequires2FA(true);
          setPending2FAAdminId(response.data.admin_id);
          setPending2FAEmail(response.data.email);
        } else {
          localStorage.setItem('adminUser', JSON.stringify(response.data.admin));
          navigate('/nakamadmm-admin/dashboard');
        }
      } else {
        throw new Error('Error de autenticación');
      }
    } catch (err) {
      setAttempts(prev => prev + 1);
      const errorMessage = err.response?.data?.detail || err.message || 'Error de conexión';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle successful 2FA verification
  const handle2FASuccess = (admin) => {
    localStorage.setItem('adminUser', JSON.stringify(admin));
    navigate('/nakamadmm-admin/dashboard');
  };

  // Go back from 2FA to login
  const handleBack2FA = () => {
    setRequires2FA(false);
    setPending2FAAdminId(null);
    setPending2FAEmail('');
    setPassword('');
  };

  // Email-based password reset
  const handleEmailReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use the same endpoint as regular users - it now supports admins
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: resetEmail,
        origin_url: window.location.origin
      });
      setEmailSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error enviando email');
    } finally {
      setLoading(false);
    }
  };

  // Secret key-based password reset
  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/admin/reset-password`, {
        email: resetEmail,
        newPassword: newPassword,
        secretKey: secretKey
      });

      if (response.data.success) {
        setResetSuccess(true);
        setTimeout(() => {
          setShowReset(false);
          setResetSuccess(false);
          setEmail(resetEmail);
          setPassword('');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error reseteando contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-700">
          <AnimatePresence mode="wait">
            {/* 2FA Verification View */}
            {requires2FA ? (
              <motion.div
                key="2fa"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <TwoFactorVerification
                  adminId={pending2FAAdminId}
                  email={pending2FAEmail}
                  onSuccess={handle2FASuccess}
                  onBack={handleBack2FA}
                />
              </motion.div>
            ) : !showReset ? (
              <motion.div
                key="login"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="flex justify-center mb-6">
                  <div className="p-4 bg-emerald-600/20 rounded-full">
                    <Shield className="w-10 h-10 text-emerald-500" />
                  </div>
                </div>
                
                <h1 className="text-2xl font-bold text-white text-center mb-2">
                  Panel de Administración
                </h1>
                <p className="text-gray-400 text-center mb-8 text-sm">
                  Acceso restringido
                </p>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-400 text-sm">{error}</p>
                      {attempts >= 3 && (
                        <p className="text-red-400/70 text-xs mt-1">
                          Advertencia: múltiples intentos detectados
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="admin-email-input"
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="admin@lokalgrow.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Contraseña
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="admin-password-input"
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-login-button"
                    className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Verificando...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5" />
                        Acceder
                      </>
                    )}
                  </button>
                </form>

                <button
                  onClick={() => { setShowReset(true); setError(''); }}
                  className="mt-4 w-full text-center text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </button>

                <p className="mt-6 text-center text-gray-500 text-xs">
                  Sistema protegido con rate limiting y logs de seguridad
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="reset"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <button
                  onClick={() => { setShowReset(false); setError(''); setHint(''); setEmailSent(false); setResetMode('email'); }}
                  className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver al login
                </button>

                <div className="flex justify-center mb-6">
                  <div className="p-4 bg-amber-600/20 rounded-full">
                    <Key className="w-10 h-10 text-amber-500" />
                  </div>
                </div>
                
                <h1 className="text-2xl font-bold text-white text-center mb-2">
                  Resetear Contraseña
                </h1>
                
                {/* Mode Toggle */}
                <div className="flex bg-gray-700 rounded-lg p-1 mb-6">
                  <button
                    onClick={() => { setResetMode('email'); setError(''); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      resetMode === 'email' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Mail className="w-4 h-4" />
                    Por Email
                  </button>
                  <button
                    onClick={() => { setResetMode('secret'); setError(''); setEmailSent(false); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      resetMode === 'secret' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Key className="w-4 h-4" />
                    Clave Secreta
                  </button>
                </div>

                {resetSuccess ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <p className="text-white text-lg">¡Contraseña actualizada!</p>
                    <p className="text-gray-400 text-sm">Redirigiendo al login...</p>
                  </div>
                ) : emailSent ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-emerald-500" />
                    </div>
                    <p className="text-white text-lg mb-2">¡Email enviado!</p>
                    <p className="text-gray-400 text-sm mb-4">
                      Revisa tu bandeja de entrada en {resetEmail}
                    </p>
                    <p className="text-gray-500 text-xs">
                      Si no recibes el email en unos minutos, revisa tu carpeta de spam
                    </p>
                    <button
                      onClick={() => { setEmailSent(false); setResetEmail(''); }}
                      className="mt-6 px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      Intentar con otro email
                    </button>
                  </div>
                ) : resetMode === 'email' ? (
                  <>
                    <p className="text-gray-400 text-center mb-6 text-sm">
                      Te enviaremos un enlace para crear una nueva contraseña
                    </p>

                    {error && (
                      <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    <form onSubmit={handleEmailReset} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email del Admin
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            required
                            className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="tu@email.com"
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Enviar enlace
                          </>
                        )}
                      </button>
                    </form>
                  </>
                ) : (
                  <>
                    <p className="text-gray-400 text-center mb-6 text-sm">
                      Introduce tu email y la clave secreta (últimos 8 caracteres de tu ID de admin)
                    </p>

                    {error && (
                      <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    <form onSubmit={handleReset} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email del Admin
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            required
                            className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="tu@email.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Clave Secreta (últimos 8 caracteres del ID)
                        </label>
                        <div className="relative">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            value={secretKey}
                            onChange={(e) => setSecretKey(e.target.value)}
                            required
                            maxLength={8}
                            minLength={8}
                            className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono tracking-wider"
                            placeholder="xxxxxxxx"
                          />
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Puedes encontrar tu clave en Admin Panel → Mi Cuenta
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Nueva Contraseña
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={6}
                            className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Mínimo 6 caracteres"
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading || secretKey.length !== 8}
                        className="w-full py-3 px-4 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-600/50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <Key className="w-5 h-5" />
                            Resetear Contraseña
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
